import { useContext, useId, useState } from 'react'
import { ExternalLink, FileText } from 'lucide-react'
import { BuilderContext } from '../BuilderContextInstance'
import { BuilderPageAutocomplete } from '../BuilderPageAutocomplete'
import Toggle from '../../shared/ui/Toggle'

const parseLink = (value) => {
  if (!value) return { url: '', target: '' }
  if (typeof value === 'object') return { url: value.url ?? '', target: value.target ?? '' }
  return { url: value, target: '' }
}

const BUILDER_PREFIX = '/builder/'

const isInternalBuilderUrl = (url) => url?.startsWith(BUILDER_PREFIX)

const slugOf = (url) => (isInternalBuilderUrl(url) ? url.slice(BUILDER_PREFIX.length) : '')

/**
 * `companion` = l'intitulé du lien, quand la déclaration en nomme le champ
 * (`labelColumn:` / `labelField:`). Un lien a deux réglages ; les régler dans
 * deux endroits différents (le texte en place, l'adresse au rail) obligeait à
 * deviner que le texte SE cliquait. La pop-in les tient tous les deux, et
 * renvoie l'intitulé en second argument de `onSave`.
 *
 * « Page du site » est une RECHERCHE à autocomplétion bornée au site de la page
 * courante, pas la liste de toutes les pages de l'instance : jusqu'ici cette
 * pop-in chargeait `/api/builder/pages` sans portée, proposait donc les pages
 * des AUTRES sites — un lien vers une page qui ne sera jamais servie sous ce
 * domaine — et téléchargeait des centaines de lignes pour n'en retenir qu'une.
 * Le contexte vient de `BuilderContext` en lecture directe (`useContext`, pas
 * `useBuilder()`) : hors builder, l'éditeur reste montable, sans site.
 */
export const LinkEditor = ({ value, companion, onSave, onClose }) => {
  const parsed = parseLink(value)
  // Une page sans site (`siteId` nul) n'a pas de contexte auquel se limiter :
  // la recherche reste ouverte à ce que l'utilisateur peut voir.
  const siteId = useContext(BuilderContext)?.page?.siteId ?? null
  const pageSearchId = useId()

  const [linkType, setLinkType] = useState(
    isInternalBuilderUrl(parsed.url) ? 'internal' : 'external'
  )
  const [url, setUrl] = useState(parsed.url)
  const [linkLabel, setLinkLabel] = useState(companion?.value ?? '')
  const [openInNewTab, setOpenInNewTab] = useState(parsed.target === '_blank')

  const handleSave = () => {
    onSave({ url, target: openInNewTab ? '_blank' : '' }, companion ? linkLabel : undefined)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose()
  }

  const handleSelectPage = (slug) => {
    setUrl(slug ? `${BUILDER_PREFIX}${slug}` : '')
  }

  return (
    <div className="flex flex-col gap-3" onKeyDown={handleKeyDown}>

      {/* Intitulé — le texte du lien, quand la déclaration dit où il vit */}
      {companion && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="builder-link-label">
            {companion.title}
          </label>
          <input
            id="builder-link-label"
            type="text"
            value={linkLabel}
            onChange={e => setLinkLabel(e.target.value)}
            placeholder="Texte du lien"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
          />
        </div>
      )}

      {/* Sélecteur de type */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <TypeTab
          active={linkType === 'external'}
          onClick={() => setLinkType('external')}
          icon={<ExternalLink size={12} />}
          label="Lien externe"
        />
        <TypeTab
          active={linkType === 'internal'}
          onClick={() => setLinkType('internal')}
          icon={<FileText size={12} />}
          label="Page du site"
        />
      </div>

      {/* URL externe */}
      {linkType === 'external' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
          <input
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
              placeholder="https://example.com"
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
          />
        </div>
      )}

      {/* Sélecteur de page interne */}
      {linkType === 'internal' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={pageSearchId}>
            Choisir une page
          </label>
          <BuilderPageAutocomplete
            id={pageSearchId}
            siteId={siteId}
            requireSite={false}
            blankLabel={null}
            value={slugOf(url)}
            onChange={handleSelectPage}
          />
        </div>
      )}

      {/* Toggle nouvel onglet — `Toggle` partagé : le pouce se place en flexbox,
          un `translate-x-*` local sort de la piste (Tailwind v4 vs UnoCSS). */}
      <div className="flex items-center gap-2.5">
        <Toggle
          checked={openInNewTab}
          onChange={() => setOpenInNewTab(v => !v)}
          label="Ouvrir dans un nouvel onglet"
        />
        <span className="text-xs text-gray-600 select-none">Ouvrir dans un nouvel onglet</span>
      </div>

      {/* Actions */}
      <div className="builder-editor-actions">
        <button
          onClick={onClose}
          className="btn btn-secondary"
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          className="btn btn-primary"
        >
          Appliquer
        </button>
      </div>
    </div>
  )
}

const TypeTab = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
      active ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    {icon}
    {label}
  </button>
)
